import React, { useCallback, memo } from 'react';
import { PlayerStats, BaseStats } from '../../types';
import { StatInput } from './StatInput';

export interface StatsUpdate {
  health?: number;
  will?: number;
  attackBonus?: number;
  defenseBonus?: number;
  speed?: number;
  isKnockedDown?: boolean;
}

export interface StatsEditorProps {
  /** Current player stats */
  stats: PlayerStats;
  /** Base stats from character for comparison */
  baseStats: BaseStats;
  /** Whether the stats are editable (own character) */
  isEditable: boolean;
  /** Callback when stats are saved - called immediately on change */
  onSave: (updates: StatsUpdate) => Promise<void>;
}

/**
 * StatsEditor Component
 * 
 * A container component for editing character stats with:
 * - Grid layout for all five stats (health, will, attack, defense, speed)
 * - Increment/decrement buttons for immediate updates
 * - Knocked down toggle switch
 * - Real-time sync via WebSocket
 * 
 * Requirements: 4.6, 4.7
 */
const StatsEditorComponent: React.FC<StatsEditorProps> = ({
  stats,
  baseStats,
  isEditable,
  onSave,
}) => {
  // Create stable callbacks for each stat to prevent unnecessary re-renders
  const handleHealthChange = useCallback((value: number) => {
    if (value !== stats.health) onSave({ health: value });
  }, [stats.health, onSave]);

  const handleWillChange = useCallback((value: number) => {
    if (value !== stats.will) onSave({ will: value });
  }, [stats.will, onSave]);

  const handleAttackChange = useCallback((value: number) => {
    if (value !== stats.attackBonus) onSave({ attackBonus: value });
  }, [stats.attackBonus, onSave]);

  const handleDefenseChange = useCallback((value: number) => {
    if (value !== stats.defenseBonus) onSave({ defenseBonus: value });
  }, [stats.defenseBonus, onSave]);

  const handleSpeedChange = useCallback((value: number) => {
    if (value !== stats.speed) onSave({ speed: value });
  }, [stats.speed, onSave]);

  // Handle knocked down toggle
  const handleKnockedDownToggle = useCallback(() => {
    onSave({ isKnockedDown: !stats.isKnockedDown });
  }, [stats.isKnockedDown, onSave]);

  return (
    <div className="stats-editor bg-gray-800/50 rounded-xl p-6 border border-gray-700">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold text-white">Stats</h3>
        {isEditable && (
          <span className="text-xs text-gray-400 flex items-center gap-1.5">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            Live sync
          </span>
        )}
      </div>

      {/* Stats Grid */}
      <div className="space-y-3">
        <StatInput
          statName="health"
          label="Health"
          value={stats.health}
          baseValue={baseStats.health}
          isEditable={isEditable}
          onChange={handleHealthChange}
        />
        <StatInput
          statName="will"
          label="Will"
          value={stats.will}
          baseValue={baseStats.will}
          isEditable={isEditable}
          onChange={handleWillChange}
        />
        <StatInput
          statName="attackBonus"
          label="Attack"
          value={stats.attackBonus}
          baseValue={baseStats.attackBonus}
          isEditable={isEditable}
          onChange={handleAttackChange}
        />
        <StatInput
          statName="defenseBonus"
          label="Defense"
          value={stats.defenseBonus}
          baseValue={baseStats.defenseBonus}
          isEditable={isEditable}
          onChange={handleDefenseChange}
        />
        <StatInput
          statName="speed"
          label="Speed"
          value={stats.speed}
          baseValue={baseStats.speed}
          isEditable={isEditable}
          onChange={handleSpeedChange}
        />
      </div>

      {/* Knocked Down Toggle (Requirement 4.7) */}
      <div className="flex items-center justify-between py-4 mt-5 border-t border-gray-700">
        <div>
          <span className="text-sm font-semibold text-gray-200">Knocked Down</span>
          <p className="text-xs text-gray-500 mt-0.5">Character is knocked down and cannot act</p>
        </div>
        {isEditable ? (
          <button
            type="button"
            role="switch"
            aria-checked={stats.isKnockedDown}
            onClick={handleKnockedDownToggle}
            className={`
              relative inline-flex h-7 w-12 items-center rounded-full
              transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-800
              ${stats.isKnockedDown ? 'bg-orange-500' : 'bg-gray-600'}
            `}
          >
            <span
              className={`
                inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm
                ${stats.isKnockedDown ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        ) : (
          <span className={`
            px-3 py-1.5 rounded-lg text-xs font-semibold
            ${stats.isKnockedDown 
              ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' 
              : 'bg-gray-700 text-gray-400 border border-gray-600'
            }
          `}>
            {stats.isKnockedDown ? 'Yes' : 'No'}
          </span>
        )}
      </div>

      {/* Eliminated Status (read-only) */}
      {stats.isEliminated && (
        <div className="flex items-center justify-between py-4 border-t border-gray-700">
          <span className="text-sm font-semibold text-gray-200">Status</span>
          <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/20 text-red-400 border border-red-500/30">
            Eliminated
          </span>
        </div>
      )}
    </div>
  );
};

// Memoize to prevent unnecessary re-renders
export const StatsEditor = memo(StatsEditorComponent);
